import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Mail, Clock, Send, BookmarkPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const fillTemplate = (text, lead) => {
  const first = (lead.name || "").split(" ")[0] || "there";
  return (text || "")
    .replaceAll("{{first_name}}", first)
    .replaceAll("{{name}}", lead.name || first)
    .replaceAll("{{email}}", lead.email || "")
    .replaceAll("{{summary}}", lead.summary ? lead.summary.toLowerCase() : "what you're working on");
};

function QuickReply({ lead, onSent }) {
  const first = (lead.name || "").split(" ")[0] || "there";
  const [subject, setSubject] = useState(`Re: your note on the site, ${first}`);
  const [body, setBody] = useState(`Hi ${first},\n\nThanks for reaching out — I saw your message about ${lead.summary ? lead.summary.toLowerCase() : "what you're working on"}.\n\nI'd love to hear a bit more. Would a quick 15-minute call this week work for you?\n\n`);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [activeTpl, setActiveTpl] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplName, setTplName] = useState("");
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    api.get("/reply-templates").then((r) => setTemplates(r.data.templates)).catch(() => {});
  }, []);

  const applyTemplate = (id) => {
    setActiveTpl(id);
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(fillTemplate(t.subject, lead));
    setBody(fillTemplate(t.body, lead));
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) return;
    setSavingTpl(true);
    // Reverse-fill so the saved template stays reusable for other leads
    const generalise = (t) => {
      let out = t;
      if (lead.summary) out = out.replaceAll(lead.summary.toLowerCase(), "{{summary}}");
      if (first && first !== "there") out = out.replaceAll(first, "{{first_name}}");
      return out;
    };
    try {
      const r = await api.post("/reply-templates", { name: tplName.trim(), subject: generalise(subject), body: generalise(body) });
      setTemplates((t) => [...t, r.data]);
      setActiveTpl(r.data.id);
      setTplName("");
      setShowSave(false);
      toast.success(`Template "${r.data.name}" saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save template");
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async () => {
    const t = templates.find((x) => x.id === activeTpl);
    if (!t) return;
    try {
      await api.delete(`/reply-templates/${t.id}`);
      setTemplates((list) => list.filter((x) => x.id !== t.id));
      setActiveTpl("");
      toast.success(`Deleted "${t.name}"`);
    } catch {
      toast.error("Couldn't delete template");
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/leads/${lead.id}/reply`, { subject, body });
      setResult(r.data.status);
      toast.success(r.data.status === "mock" ? "Reply recorded (email delivery not configured yet)" : `Reply sent to ${lead.email}`);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#e5e2da] bg-white p-6" data-testid="lead-quick-reply">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Quick reply</div>
        <div className="text-xs text-[#5c5c5c]">To {lead.email}</div>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Select value={activeTpl} onValueChange={applyTemplate}>
          <SelectTrigger data-testid="reply-template-select" className="bg-white border-[#e5e2da] w-[260px] h-9 text-sm">
            <SelectValue placeholder={templates.length ? "Use a saved template…" : "No templates yet"} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {templates.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`reply-template-option-${t.id}`}>{t.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeTpl && (
          <button onClick={deleteTemplate} data-testid="reply-template-delete-btn" className="inline-flex items-center gap-1 text-xs text-[#5c5c5c] hover:text-red-700" title="Delete this template">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        )}
        <button onClick={() => setShowSave((s) => !s)} data-testid="reply-template-save-toggle" className="ml-auto inline-flex items-center gap-1 text-xs text-[#163325] hover:underline underline-offset-4">
          <BookmarkPlus className="w-3.5 h-3.5" /> Save as template
        </button>
      </div>
      {showSave && (
        <div className="mt-2 flex items-center gap-2 rounded-md bg-[#f9f8f6] border border-[#e5e2da] p-2" data-testid="reply-template-save-row">
          <input
            data-testid="reply-template-name-input"
            className="flex-1 rounded-md border border-[#e5e2da] bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#163325]/30"
            placeholder="Template name, e.g. Book a discovery call"
            value={tplName}
            onChange={(e) => setTplName(e.target.value)}
            maxLength={60}
          />
          <Button size="sm" onClick={saveTemplate} disabled={savingTpl || !tplName.trim()} data-testid="reply-template-save-btn" className="rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6]">
            {savingTpl ? "Saving…" : "Save"}
          </Button>
          <span className="text-[11px] text-[#5c5c5c] hidden sm:block">Tip: use {"{{first_name}}"}, {"{{summary}}"} as placeholders</span>
        </div>
      )}
      <input
        data-testid="lead-reply-subject"
        className="mt-3 w-full rounded-md border border-[#e5e2da] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#163325]/30"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        maxLength={200}
      />
      <Textarea data-testid="lead-reply-body" rows={6} value={body} onChange={(e) => setBody(e.target.value)} className="mt-2 bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="text-xs text-[#5c5c5c]">{result === "mock" ? "Saved to the lead's timeline. Connect Resend in Settings to deliver for real." : result === "sent" ? "Delivered." : "Marks the lead as contacted."}</div>
        <Button data-testid="lead-reply-send-btn" onClick={send} disabled={sending || !subject.trim() || !body.trim()} className="rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] px-5">
          <Send className="w-3.5 h-3.5 mr-2" /> {sending ? "Sending…" : "Send reply"}
        </Button>
      </div>
    </div>
  );
}

export default function LeadDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/leads/${id}`).then((r) => {
    setData(r.data);
    setStatus(r.data.lead.status);
    setNotes(r.data.lead.notes || "");
  });

  useEffect(() => {
    load();
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/leads/${id}`, { status, notes });
      toast.success("Lead updated");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-10 text-[#5c5c5c]">Loading…</div>;

  const { lead, messages, email_sends } = data;

  return (
    <div className="p-8 lg:p-10" data-testid="lead-detail">
      <Link to="/app/leads" className="inline-flex items-center gap-2 text-sm text-[#5c5c5c] hover:text-[#163325]" data-testid="back-to-leads">
        <ArrowLeft className="w-4 h-4" /> Back to leads
      </Link>
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-lg border border-[#e5e2da] bg-white p-6">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Lead</div>
                <h1 className="font-serif-display text-3xl mt-1">{lead.name}</h1>
                <div className="text-sm text-[#5c5c5c]">{lead.email}</div>
              </div>
            </div>
            {lead.summary && <p className="mt-4 text-sm text-[#3c3c3c] border-l-2 border-[#d4a373] pl-3 italic">{lead.summary}</p>}
          </div>

          <QuickReply lead={lead} onSent={load} />

          <div className="rounded-lg border border-[#e5e2da] bg-white p-6">
            <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Conversation</div>
            {messages.length === 0 ? (
              <div className="mt-4 text-sm text-[#5c5c5c]">No chat transcript (captured via form).</div>
            ) : (
              <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto dash-scroll pr-2">
                {messages.map((m, i) => (
                  <div key={i} className={`max-w-[80%] text-sm ${m.role === "user" ? "ml-auto" : ""}`}>
                    <div
                      className="px-3 py-2 rounded-2xl"
                      style={m.role === "user"
                        ? { background: "#163325", color: "#f9f8f6", borderBottomRightRadius: 4 }
                        : { background: "#ebe8e0", color: "#1c1c1c", borderBottomLeftRadius: 4 }
                      }
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-[#e5e2da] bg-white p-6" data-testid="email-timeline">
            <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Email follow-up</div>
            {email_sends.length === 0 ? (
              <div className="mt-4 text-sm text-[#5c5c5c]">No emails scheduled.</div>
            ) : (
              <ul className="mt-4 space-y-3">
                {email_sends.map((e) => (
                  <li key={e.id} className="flex items-start gap-3 border-l-2 pl-3" style={{ borderColor: e.status === "sent" ? "#163325" : "#d4a373" }}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0`} style={{ background: e.status === "sent" ? "#163325" : "#ebe8e0", color: e.status === "sent" ? "#f9f8f6" : "#163325" }}>
                      {e.status === "sent" ? <Mail className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{e.subject}{e.kind === "manual_reply" && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#d4a373]">Your reply</span>}</div>
                      <div className="text-xs text-[#5c5c5c]">{e.status === "sent" ? "Sent" : e.status === "mock" ? "Recorded (delivery not configured)" : e.status === "failed" ? "Failed to send" : `Scheduled for ${e.scheduled_for?.slice(0, 16).replace("T", " ")}`}</div>
                      <div className="text-xs text-[#5c5c5c] mt-1 line-clamp-2 whitespace-pre-line">{e.body}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-1 space-y-4">
          <div className="rounded-lg border border-[#e5e2da] bg-white p-6" data-testid="lead-actions">
            <div className="text-xs uppercase tracking-widest text-[#8c9b86]">Actions</div>
            <div className="mt-4 space-y-3">
              <div>
                <div className="text-xs text-[#5c5c5c] mb-1">Status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="lead-status-select" className="bg-white border-[#e5e2da]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["new", "qualified", "contacted", "booked", "won", "lost"].map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-[#5c5c5c] mb-1">Private notes</div>
                <Textarea data-testid="lead-notes-input" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-white border-[#e5e2da] focus-visible:ring-[#163325]" />
              </div>
              <Button data-testid="lead-save-btn" onClick={save} disabled={saving} className="w-full rounded-md bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6]">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
